/**
 * Social Analytics — Learn-Mode inline tips.
 *
 * Covers `routes/social/_sections/analytics/OverviewTab.tsx` +
 * `PlatformsTab.tsx`, `RetentionTab.tsx`, `GrowthTab.tsx` and
 * `components/analytics/DataHealthPanel.tsx`.
 * `analytics-top-posts`, `analytics-best-time`, and `analytics-content-types`
 * are passed as a `helpContext` prop to the local `<Panel>` wrapper, which
 * spreads it onto a dynamic `data-help-context={helpContext}` — not a
 * literal attribute in source, so they're easy to miss on a plain grep.
 */

import type { LearnTip } from "./types";

export const analyticsTips: LearnTip[] = [
  {
    key: "analytics-data-health",
    title: "Data Health",
    body: "Flags when the nightly analytics sync is behind or an account's numbers can't be trusted, so you know when the figures below might be out of date before you report on them.",
  },
  {
    key: "analytics-export-csv",
    title: "Export CSV",
    body: "Downloads this overview's numbers as a spreadsheet for the selected date range — useful for building a custom report or archiving the data outside the app.",
  },
  {
    key: "analytics-overview-kpis",
    title: "Key Metrics",
    body: "The headline numbers for the selected period and client — followers, reach, engagement, and posts — compared against the previous period.",
  },
  {
    key: "analytics-views-kpi",
    title: "Views",
    body: "How many times the posts published in this period were viewed — Meta's headline metric, which replaced impressions. Instagram and TikTok share view counts; a platform that doesn't shows \"Not available\" instead of a zero.",
  },
  {
    key: "analytics-stories-kpi",
    title: "Instagram Stories",
    body: "How many Instagram Stories went up in this period, with their total views underneath. Stories are counted separately from posts and only appear when at least one story was captured; Instagram removes a story's numbers 24 hours after posting, so they are saved while the story is live.",
  },
  {
    key: "analytics-trends",
    title: "Trends",
    body: "Day-by-day charts for followers and engagement over the period, so you can spot spikes, dips, or a steady trend at a glance.",
  },
  {
    key: "analytics-by-account",
    title: "By Account",
    body: "Breaks the same metrics down per connected account, so you can see which specific platform or profile is driving the overall numbers.",
  },
  {
    key: "analytics-top-posts",
    title: "Top Posts This Period",
    body: "Every post that went live in this period — whether published from here or posted natively — ranked by engagement, so you can see what resonated most.",
  },
  {
    key: "analytics-best-time",
    title: "Best Time to Post",
    body: "Shows which days and hours get the most engagement for the reach a post got, based on this client's own posts over the past 12 months. Darker = better than the account's typical post; hatched squares have fewer than 3 posts, so treat them as a hint, not a verdict.",
  },
  {
    key: "analytics-content-types",
    title: "Content-Type Performance",
    body: "Compares which post formats (photo, carousel, video/Reel) earn the most engagement per post in this period.",
  },
  {
    key: "analytics-platform-kpis",
    title: "Platform Metrics",
    body: "The headline numbers for just the currently selected platform's connected accounts.",
  },
  {
    key: "analytics-retention-dormant",
    title: "Dormant (Connected, Not Publishing)",
    body: "Flags a client whose social accounts are connected but who has had nothing published in the last 21 days and nothing scheduled. They're set up but see no output, which is the pattern most likely to end in a cancellation, so the brand is raised to at least High risk.",
  },
  {
    key: "analytics-retention-onboarding",
    title: "First 14 Days",
    body: "Every new client should see their first post go live within 14 days of being set up. This card follows each newly added brand from day 0 and shows the next step needed to get that first visible win out the door. A brand turns At risk on day 7 and Overdue on day 14 if nothing has been published yet.",
  },
  {
    key: "analytics-retention-onboarding-day",
    title: "Onboarding Day",
    body: "How many days ago the brand was set up. Day 0 is the day it was added. The target is a first publish by day 7 and no later than day 14.",
  },
  {
    key: "analytics-retention-onboarding-milestones",
    title: "Onboarding Milestones",
    body: "The five steps to a first visible win, in order: a connected social account, a filled-in brand kit, the first draft, client approval, and the first published post. Green means done and amber is the step holding things up right now.",
  },
  {
    key: "analytics-platform-daily",
    title: "Daily Snapshots",
    body: "A day-by-day table of follower counts per account for this platform, newest first — useful for spotting exactly when a jump or drop happened.",
  },
  {
    key: "analytics-posts-table",
    title: "Posts",
    body: "Every post that went live in the selected period, native or published from here, with its engagements, reach, views and engagement rate. Sort and filter by platform or format; Instagram Reels also show average watch time and the share of viewers who skipped in the first 3 seconds. Stories are not listed here.",
  },
  {
    key: "analytics-posts-export",
    title: "Export Posts CSV",
    body: "Downloads the posts on this page, with their metrics and links, as a spreadsheet.",
  },
  {
    key: "analytics-competitors",
    title: "Competitors",
    body: "How this client compares with the competitors they track, using public profile numbers captured once a day. History starts the day a competitor is added.",
  },
  {
    key: "analytics-competitors-leaderboard",
    title: "Competitor Leaderboard",
    body: "Followers, posting frequency and public engagement rate (average likes and comments per post divided by followers). The client's own row uses the same public formula so the comparison is fair, and it can differ from the engagement rate in Overview, which uses private reach.",
  },
  {
    key: "analytics-competitors-manage",
    title: "Tracked Competitors",
    body: "Add up to 10 competitors per client by Instagram username or Bluesky handle. Instagram competitors must be public business or creator accounts and are read through this client's own Instagram connection.",
  },
  {
    key: "analytics-reel-performance",
    title: "Reel Performance",
    body: "Views, average watch percentage, and shares for recently published renders, plus the same numbers frozen at +24 hours, +72 hours, and 7 days after publish as they're captured. The breakdown below ranks recipes and framing styles by average watch percentage — with too few renders to compare yet, it's flagged \"insufficient sample\" rather than guessed at.",
  },
  {
    key: "analytics-web-impact",
    title: "Website Impact",
    body: "Sessions, engaged sessions and conversions on the client's website that Google Analytics attributes to each social network, for posts tagged with a tracking link (and, as a fallback, the sessions Google Analytics itself buckets as organic social). Requires the client's Google Analytics property to be linked — shows a clear message instead of zeros when it isn't.",
  },
  {
    key: "analytics-benchmarks",
    title: "Industry Benchmarks",
    body: "This client's engagement rate, posting cadence and follower growth against the median and typical range (P25–P75) of other businesses we work with in the same industry, computed once a month. Shown only once at least 5 similar businesses have reported data that month — individual businesses are never identified.",
  },
  {
    key: "analytics-growth-kpis",
    title: "Growth Headline Numbers",
    body: "Follower growth and reply-rate SLA for the selected window, each compared against the same-length window right before it — the client-facing proof that things are moving. Median response time is flagged when it's over the 5-minute comment-reply target.",
  },
  {
    key: "analytics-growth-trend",
    title: "Follower Trend",
    body: "Total followers across every connected account, summed per week, for the selected window — a quick visual of whether growth is steady, flat, or dipping.",
  },
  {
    key: "analytics-growth-accounts",
    title: "Growth By Account",
    body: "Follower change per connected account, this window and the one right before it, plus the reach and engagement each account generated — so a growth spike or drop can be traced to a specific platform.",
  },
  {
    key: "analytics-growth-reply-sla",
    title: "Reply SLA",
    body: "Comment reply rate, median and 90th-percentile response time, and the current open-and-unreplied count, broken out per client brand. A median over 5 minutes is flagged so a slipping brand is visible before a client notices.",
  },
  {
    key: "analytics-growth-dm",
    title: "Direct Messages",
    body: "Messenger and Instagram Direct conversation volume and first-response time per brand. Reads as connecting-soon rather than zero until Meta's messaging permissions are approved for DM ingest.",
  },
  {
    key: "analytics-audience",
    title: "Audience",
    body: "Where this account's followers are and how old they are, as reported by the platform. The headline figure is the share of followers in the client's own city.",
    bullets: [
      "The local share needs the client's city on their CRM record — add it there if you see a prompt instead of a percentage.",
      "“Not reported” means the platform hasn't shared that breakdown yet (small accounts often don't get one).",
    ],
  },
  {
    key: "analytics-campaign-tags",
    title: "Campaign Performance",
    body: "Each row totals the posts that carry one campaign tag, ranked by engagement, so you can see which campaigns actually earned attention.",
    bullets: [
      "Add tags while composing a post; untagged posts don't appear here.",
      "A post with several tags counts toward each of them.",
    ],
  },
];
